import clsx from 'clsx';
import * as React from 'react';
import { usePress } from 'react-aria';

import type * as api from '@gitbook/api-client';
import { type Collection, resolveContentIcon, type Space } from '@gitbook/app-database';
import { sizes } from '@gitbook/react-uikit';
import { Emoji } from '@gitbook/spine-react';

import { getImageResizedURL } from '../../utils';

const EMOJI_BACKGROUND_FACTOR = 2;

export type ContentIconProps = {
    content: Space | api.Space | Collection;
    /** Whether the icon should be rendered with a background or not, defaults to true */
    withBackground?: boolean;
    size?: number;
    className?: string;
};

/**
 * Icon for content.
 */
export function ContentIcon(props: ContentIconProps): React.ReactElement {
    const { content, size = sizes.S, className, withBackground = true } = props;

    const { iconURL, emoji } = resolveContentIcon(content);

    return (
        <ControlledContentIcon
            iconURL={iconURL}
            emoji={emoji}
            fallbackText={content.title}
            size={size}
            className={className}
            withBackground={withBackground}
        />
    );
}

/**
 * Icon for content,when directly passing iconURL / emoji.
 */
export const ControlledContentIcon = React.forwardRef(function ControlledContentIcon(
    props: {
        iconURL: string | undefined;
        emoji: string | undefined;
        fallbackText: string;
        /** Whether the icon should be rendered with a background or not, defaults to true */
        withBackground?: boolean;
        size?: number;
        className?: string;
        style?: React.CSSProperties;
        itemStyle?: React.CSSProperties;
        /** When passed, a role and focus states will also apply */
        onPress?: () => void;
    },
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const {
        iconURL,
        emoji,
        fallbackText,
        size = sizes.S,
        className,
        withBackground = true,
        onPress,
        style,
        itemStyle,
    } = props;
    const emojiSize = getEmojiSize(size, withBackground);
    const { pressProps } = usePress({
        onPress,
    });

    return (
        <div
            ref={ref}
            className={clsx(
                'relative flex items-center justify-center rounded transition',
                onPress && 'cursor-pointer focus-visible:shadow-focus-ring',
                withBackground && 'overflow-hidden border border-base',
                withBackground && !style?.background && 'bg-muted',
                className
            )}
            {...(onPress && {
                role: 'button',
                tabIndex: 0,
                ...pressProps,
            })}
            style={
                {
                    width: size,
                    height: size,
                    '--emoji-bg-factor': EMOJI_BACKGROUND_FACTOR,
                    ...style,
                } as React.CSSProperties
            }
        >
            {iconURL ? (
                <img
                    src={getImageResizedURL(iconURL, {
                        width: size,
                        height: size,
                        fit: 'contain',
                    })}
                    className="rounded object-contain"
                    style={{ width: size, height: size }}
                    alt=""
                />
            ) : emoji && emojiSize > 0 ? (
                <>
                    <Emoji code={emoji} size={emojiSize} />
                    {withBackground && (
                        <div className="absolute flex">
                            <Emoji
                                code={emoji}
                                size={size * EMOJI_BACKGROUND_FACTOR}
                                className="opacity-20 dark:opacity-5"
                                style={{
                                    // Filter does not work with TW for a reason I ignore, maybe !important
                                    filter: 'blur(calc(var(--emoji-bg-factor, 2) * 8px)) contrast(110%) hue-rotate(10deg) brightness(1.2) saturate(1.2)',
                                }}
                            />
                        </div>
                    )}
                </>
            ) : emojiSize > 0 ? (
                <FallbackIcon fallbackText={fallbackText} size={size} style={itemStyle} />
            ) : null}
        </div>
    );
});

function FallbackIcon(props: { fallbackText: string; size: number; style?: React.CSSProperties }) {
    const { size, fallbackText, style } = props;
    const letter = fallbackText.slice(0, 1).toUpperCase();
    return (
        // biome-ignore lint/a11y/noSvgWithoutTitle: we don't want to add a fallback on this
        <svg
            viewBox="0 0 100 100"
            width={size}
            height={size}
            xmlns="http://www.w3.org/2000/svg"
            style={style}
        >
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dy=".35em"
                fontSize="80"
                className="fill-[currentColor] font-bold font-system"
            >
                {letter}
            </text>
        </svg>
    );
}

function getEmojiSize(iconSize: number, withBackground: boolean): number {
    return withBackground ? iconSize * 0.7 : iconSize;
}
